import {
    AddIcon,
    Button,
    ButtonIcon,
    ButtonText,
    Checkbox,
    CheckboxIcon,
    CheckboxIndicator,
    CheckboxLabel,
    FormField,
    HStack,
    SelectGeneric,
    Text,
    VStack
} from "@hosttools/frontend";
import type React from "react";
import {memo, useCallback, useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";

import {bedTypeOptions} from "../constant";
import type {Bed, Bedroom} from "../type";

import BedForm from "./BedForm";
import BedItem from "./BedItem";

import Label from "@/client/components/Label";
import {Table, TableHeader, TableRow} from "@/client/components/Table";
import {Card, CardBody, CardFooter} from "@/client/features/ListingContent/Card";

type Props = {
    bedroom: Bedroom;
    index?: number;
    onChange: (bedroom: Bedroom, index?: number) => void;
    onCancel: () => void;
};

const BedroomForm: React.FC<Props> = ({bedroom, index, onChange, onCancel}) => {
    const [bedTemp, setBedTemp] = useState<Partial<Bed> | undefined>();
    const {
        handleSubmit,
        setValue,
        getValues,
        reset,
        formState: {errors},
        control
    } = useForm<Bedroom>({
        defaultValues: bedroom
    });

    const handleAddNewBed = useCallback(() => {
        setBedTemp({
            bedType: "RMA102",
            count: undefined
        });
    }, []);

    const handleSaveBed = useCallback(
        (bed: Bed, index?: number) => {
            const beds = getValues("beds");
            const newBeds = {...(beds || {bed: []})};
            if (typeof index !== "undefined") {
                newBeds.bed[index] = bed;
            } else {
                newBeds.bed.push(bed);
            }

            setValue("beds", newBeds);
            setBedTemp(undefined);
        },
        [getValues, setValue]
    );

    const handleRemoveBed = useCallback(
        (index: number) => {
            const beds = getValues("beds");

            const newBeds = {...(beds || {bed: []})};
            newBeds.bed = newBeds.bed.filter((_, i) => i !== index);
            setValue("beds", newBeds);
        },
        [getValues, setValue]
    );

    const handleCancelBed = useCallback(() => {
        setBedTemp(undefined);
    }, []);

    const handleSave = useCallback(
        (bedroom: Bedroom) => {
            onChange(bedroom, index);
        },
        [index, onChange]
    );

    useEffect(() => {
        reset(bedroom);
    }, [bedroom, reset]);

    return (
        <Card>
            <CardBody>
                <VStack space="xl">
                    <FormField
                        className="w-full md:w-1/3"
                        error={errors.type?.message}
                        label="Room type"
                    >
                        <Controller
                            control={control}
                            name="type"
                            render={({field}) => {
                                return (
                                    <SelectGeneric
                                        options={bedTypeOptions}
                                        placeholder="Select room..."
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                );
                            }}
                            rules={{
                                required: "This field is required"
                            }}
                        />
                    </FormField>
                    <FormField>
                        <Controller
                            control={control}
                            defaultValue={false}
                            name="privateBathroom"
                            render={({field}) => {
                                return (
                                    <Checkbox
                                        isChecked={field.value}
                                        value={field.name}
                                        onChange={field.onChange}
                                    >
                                        <CheckboxIndicator>
                                            <CheckboxIcon />
                                        </CheckboxIndicator>
                                        <CheckboxLabel>Is private bathroom</CheckboxLabel>
                                    </Checkbox>
                                );
                            }}
                        />
                    </FormField>
                    <FormField className="w-full" error={errors.beds?.message} name="beds.bed">
                        <Label>
                            <HStack className="w-full items-center justify-between">
                                <Text color="blueGray.400" variant="sm">
                                    Beds
                                </Text>
                                {!bedTemp && (
                                    <Button
                                        className="self-end"
                                        testID="btn-add-new-bed"
                                        variant="ghost"
                                        onPress={handleAddNewBed}
                                    >
                                        <ButtonIcon as={AddIcon} />
                                        <ButtonText>New bed</ButtonText>
                                    </Button>
                                )}
                            </HStack>
                        </Label>
                        <Controller
                            control={control}
                            name="beds"
                            render={({field}) => {
                                return (
                                    <Table overflow="visible">
                                        <TableRow>
                                            <TableHeader>Bed type</TableHeader>
                                            <TableHeader>Count</TableHeader>
                                            <TableHeader align="end">Action</TableHeader>
                                        </TableRow>
                                        {field.value.bed.map((bed, index) => (
                                            <BedItem
                                                key={bed.bedType}
                                                bed={bed}
                                                index={index}
                                                onChange={handleSaveBed}
                                                onRemove={handleRemoveBed}
                                            />
                                        ))}
                                        {bedTemp && (
                                            <BedForm
                                                bed={bedTemp}
                                                onCancel={handleCancelBed}
                                                onChange={handleSaveBed}
                                            />
                                        )}
                                    </Table>
                                );
                            }}
                            rules={{
                                validate: value => {
                                    if (value.bed.length === 0) {
                                        return "Please add at least one bed.";
                                    }
                                    return true;
                                }
                            }}
                        />
                    </FormField>
                </VStack>
            </CardBody>
            <CardFooter>
                <HStack space="sm">
                    <Button variant="outline" onPress={onCancel}>
                        <ButtonText>Close</ButtonText>
                    </Button>
                    <Button
                        testID="btn-add-room"
                        variant="solid"
                        onPress={handleSubmit(handleSave)}
                    >
                        <ButtonText>{typeof index !== "undefined" ? "Save" : "Add"}</ButtonText>
                    </Button>
                </HStack>
            </CardFooter>
        </Card>
    );
};

export default memo(BedroomForm);
